/* Workshop interactions only. No scroll/entrance fading. */
(() => {
  'use strict';
  if (window.MRWorkshopContent) return;
  window.MRWorkshopContent = true;

  function initRoot(root) {
    if (!root || root.dataset.ctcReady === 'true') return;
    root.dataset.ctcReady = 'true';

    // Shared by every tour-rebuild template; no other tour blocks are changed.
    const initCtcGallery = (gallery) => {
      if (gallery.dataset.ctcGalleryReady === 'true') return;
      const viewport = gallery.querySelector('[data-ctc-gallery-viewport]');
      const track = gallery.querySelector('[data-ctc-gallery-track]');
      if (!viewport || !track) return;
      const originals = Array.from(track.querySelectorAll('[data-ctc-slide]'));
      const count = originals.length;
      if (!count) return;
      gallery.dataset.ctcGalleryReady = 'true';
      const previous = gallery.querySelector('[data-ctc-prev]');
      const next = gallery.querySelector('[data-ctc-next]');
      const progress = gallery.querySelector('[data-ctc-gallery-progress]');
      const counter = gallery.querySelector('[data-ctc-gallery-current]');
      const controls = gallery.querySelector('.ctc-gallery__controls');
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      const modulo = (index) => ((index % count) + count) % count;
      const centerOffset = count > 1 ? count : 0;
      let current = 0, position = centerOffset, width = 0, step = 0, offset = 0;
      let drag = null, moved = false, moving = false, disposed = false;
      let timer = 0, movementId = 0, queue = [];

      if (count > 1) {
        const before = document.createDocumentFragment();
        const after = document.createDocumentFragment();
        originals.forEach((slide) => {
          const left = slide.cloneNode(true), right = slide.cloneNode(true);
          left.setAttribute('data-ctc-clone', '');
          right.setAttribute('data-ctc-clone', '');
          before.appendChild(left); after.appendChild(right);
        });
        track.insertBefore(before, track.firstChild);
        track.appendChild(after);
      }
      const slides = Array.from(track.querySelectorAll('[data-ctc-slide]'));
      const images = slides.map((slide) => slide.querySelector('img'));
      slides.forEach((slide, slot) => {
        slide.dataset.ctcSlot = String(slot);
        slide.setAttribute('role', 'group');
        slide.setAttribute('aria-roledescription', 'slide');
        slide.setAttribute('aria-label', `${modulo(slot) + 1} of ${count}`);
      });
      const warm = (index) => {
        const logicalIndex = modulo(index);
        images.forEach((image, slot) => {
          if (!image || modulo(slot) !== logicalIndex) return;
          // Match the frame, not the entire viewport, before hydrating source candidates.
          image.sizes = `${Math.max(1, Math.ceil(width || 480))}px`;
          image.loading = 'eager';
          if (image.dataset.srcset) {
            image.srcset = image.dataset.srcset;
            delete image.dataset.srcset;
          }
          if (image.dataset.src) {
            image.src = image.dataset.src;
            delete image.dataset.src;
          }
        });
      };
      const ready = (index) => {
        warm(index);
        return Promise.all(images.filter((image, slot) => image && modulo(slot) === modulo(index)).map((image) => {
          if (image.complete && image.naturalWidth) return Promise.resolve(true);
          if (typeof image.decode === 'function') return image.decode().then(() => true).catch(() => Boolean(image.complete && image.naturalWidth));
          return new Promise((resolve) => {
            if (image.complete) { resolve(Boolean(image.naturalWidth)); return; }
            const finish = () => {
              image.removeEventListener('load', finish); image.removeEventListener('error', finish);
              resolve(Boolean(image.naturalWidth));
            };
            image.addEventListener('load', finish); image.addEventListener('error', finish);
          });
        })).then((results) => results.every(Boolean));
      };
      const translate = (extra = 0) => { track.style.transform = `translate3d(${offset - position * step + extra}px,0,0)`; };
      const update = () => {
        if (counter) counter.textContent = String(current + 1).padStart(2, '0');
        if (progress) {
          progress.value = current + 1;
          progress.max = count;
          progress.style.setProperty('--ctc-gallery-progress', `${count > 1 ? current / (count - 1) * 100 : 0}%`);
          progress.setAttribute('aria-valuetext', `Image ${current + 1} of ${count}`);
        }
        slides.forEach((slide, slot) => {
          slide.classList.toggle('is-current', slot === position);
          slide.setAttribute('aria-hidden', String(slot !== position));
          slide.inert = Math.abs(slot - position) > 1;
        });
        warm(current - 1); warm(current); warm(current + 1);
      };
      const reposition = () => {
        track.style.transition = 'none';
        slides.forEach((slide) => { slide.style.transition = 'none'; });
        position = centerOffset + current;
        translate(); update();
        void track.offsetWidth;
        slides.forEach((slide) => { slide.style.transition = ''; });
        track.style.transition = '';
      };
      const drain = () => {
        if (disposed || moving || drag || !queue.length) return;
        const action = queue.shift();
        if ('target' in action) {
          let distance = modulo(action.target - current);
          if (distance > count / 2) distance -= count;
          if (!distance) { drain(); return; }
          move(distance);
        } else move(action.distance);
      };
      const finish = () => {
        if (!moving || disposed) return;
        clearTimeout(timer); moving = false;
        reposition(); requestAnimationFrame(drain);
      };
      const move = (distance) => {
        if (disposed || count < 2) return;
        moving = true;
        const ticket = ++movementId;
        const target = modulo(current + distance);
        for (let n = 0; n <= Math.abs(distance) + 1; n += 1) warm(current + n * Math.sign(distance));
        const begin = () => {
          if (disposed || ticket !== movementId) return;
          position += distance; current = target;
          track.style.transition = ''; translate(); update();
          if (reducedMotion.matches) { finish(); return; }
          timer = setTimeout(finish, 720);
        };
        const targetImages = images.filter((image, slot) => image && modulo(slot) === target);
        if (targetImages.every((image) => image.complete && image.naturalWidth)) { begin(); return; }
        ready(target).then((loaded) => {
          if (disposed || ticket !== movementId) return;
          if (loaded) begin();
          else { moving = false; reposition(); requestAnimationFrame(drain); }
        });
      };
      const advance = (distance) => { if (count > 1) { queue.push({ distance }); drain(); } };
      const select = (target) => { if (count > 1) { queue = [{ target: modulo(target) }]; drain(); } };
      const layout = () => {
        if (disposed || !viewport.clientWidth || !viewport.clientHeight) return;
        clearTimeout(timer); movementId += 1; moving = false;
        if (drag && viewport.hasPointerCapture(drag.id)) viewport.releasePointerCapture(drag.id);
        drag = null; viewport.classList.remove('is-dragging');
        width = Math.min(viewport.clientHeight * 966 / 1110, viewport.clientWidth);
        viewport.style.setProperty('--ctc-gallery-frame-width', `${width}px`);
        step = width + 8; offset = (viewport.clientWidth - width) / 2;
        slides.forEach((slide, slot) => { slide.style.width = `${width}px`; slide.style.left = `${slot * step}px`; });
        viewport.classList.add('is-enhanced');
        reposition(); requestAnimationFrame(drain);
      };
      track.addEventListener('transitionend', (event) => { if (event.target === track && event.propertyName === 'transform') finish(); });
      previous?.addEventListener('click', () => advance(-1));
      next?.addEventListener('click', () => advance(1));
      progress?.addEventListener('input', () => select(Number(progress.value) - 1));
      track.addEventListener('click', (event) => {
        const slide = event.target.closest('[data-ctc-slide]');
        if (slide && !moved && !moving) select(modulo(Number(slide.dataset.ctcSlot)));
      });
      viewport.addEventListener('keydown', (event) => {
        if (event.target === progress) return;
        if (event.key === 'ArrowRight') { event.preventDefault(); advance(1); }
        if (event.key === 'ArrowLeft') { event.preventDefault(); advance(-1); }
      });
      viewport.addEventListener('pointerdown', (event) => {
        if (moving || count < 2 || event.button !== 0 || event.target.closest('.ctc-gallery__controls, .ctc-gallery__arrow')) return;
        drag = { x: event.clientX, y: event.clientY, id: event.pointerId, distance: 0, active: false };
        moved = false;
      });
      viewport.addEventListener('pointermove', (event) => {
        if (!drag || event.pointerId !== drag.id) return;
        const dx = event.clientX - drag.x, dy = event.clientY - drag.y;
        if (!drag.active) {
          if (Math.abs(dy) > Math.abs(dx) && Math.abs(dy) > 9) { drag = null; drain(); return; }
          if (Math.abs(dx) < 8) return;
          drag.active = true; moved = true;
          viewport.setPointerCapture(event.pointerId); viewport.classList.add('is-dragging'); track.style.transition = 'none';
        }
        drag.distance = dx; translate(Math.sign(dx) * Math.min(Math.abs(dx), step));
      });
      const release = (event, cancelled = false) => {
        if (!drag || event.pointerId !== drag.id) return;
        const distance = cancelled ? 0 : drag.distance, active = drag.active;
        drag = null; viewport.classList.remove('is-dragging');
        if (viewport.hasPointerCapture(event.pointerId)) viewport.releasePointerCapture(event.pointerId);
        if (active) move(Math.abs(distance) > width * .15 ? (distance < 0 ? 1 : -1) : 0);
        else drain();
        setTimeout(() => { moved = false; }, 0);
      };
      viewport.addEventListener('pointerup', (event) => release(event));
      viewport.addEventListener('pointercancel', (event) => release(event, true));
      let observer = null;
      if (typeof ResizeObserver !== 'undefined') { observer = new ResizeObserver(layout); observer.observe(viewport); }
      else window.addEventListener('resize', layout);
      const unload = (event) => {
        if (!event.target.contains(gallery)) return;
        disposed = true; movementId += 1; queue = []; clearTimeout(timer);
        observer?.disconnect(); window.removeEventListener('resize', layout);
        document.removeEventListener('shopify:section:unload', unload);
      };
      document.addEventListener('shopify:section:unload', unload);
      [previous, next, controls].forEach((element) => { if (element) element.hidden = count < 2; });
      layout();
    };
    // Do not clone slides, measure frames or fetch adjacent photographs while offscreen.
    const galleries = Array.from(root.querySelectorAll('[data-ctc-gallery]'));
    const nearby = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) { nearby.unobserve(entry.target); initCtcGallery(entry.target); }
      });
    }, { rootMargin: '400px 0px' }) : null;
    galleries.forEach(gallery => {
      if (nearby) nearby.observe(gallery); else initCtcGallery(gallery);
      gallery.addEventListener('pointerenter', () => initCtcGallery(gallery), { once: true, passive: true });
      gallery.addEventListener('focusin', () => initCtcGallery(gallery), { once: true });
    });
    const stopObserving = event => {
      if (!event.target.contains(root)) return;
      nearby?.disconnect();
      document.removeEventListener('shopify:section:unload', stopObserving);
    };
    document.addEventListener('shopify:section:unload', stopObserving);

    root.querySelectorAll('[data-ctc-accordion-item]').forEach((item) => {
      const button = item.querySelector('.ctc-accordion__button');
      button?.addEventListener('click', () => {
        const nextState = !item.classList.contains('is-open');
        item.classList.toggle('is-open', nextState);
        button.setAttribute('aria-expanded', String(nextState));
      });
    });

    root.querySelectorAll('[data-ctc-tabs]').forEach((tabs) => {
      const buttons = [...tabs.querySelectorAll('[data-tab]')];
      const panels = [...tabs.querySelectorAll('[data-tabpanel]')];
      buttons.forEach((button) => button.addEventListener('click', () => {
        const selected = button.dataset.tab;
        buttons.forEach((item) => { const active = item === button; item.classList.toggle('is-active', active); item.setAttribute('aria-selected', String(active)); });
        panels.forEach((panel) => { panel.hidden = panel.dataset.tabpanel !== selected; });
      }));
    });

    root.querySelectorAll('[data-ctc-testimonials]').forEach((slider) => {
      const cards = [...slider.querySelectorAll('[data-ctc-testimonial]')];
      const dots = [...slider.querySelectorAll('[data-ctc-testimonial-dot]')];
      if (!cards.length) return;
      let current = 0;
      const render = () => { cards.forEach((card,index) => card.classList.toggle('is-current', index === current)); dots.forEach((dot,index) => dot.classList.toggle('is-active', index === current)); };
      slider.querySelector('[data-ctc-testimonial-prev]')?.addEventListener('click', () => { current = (current - 1 + cards.length) % cards.length; render(); });
      slider.querySelector('[data-ctc-testimonial-next]')?.addEventListener('click', () => { current = (current + 1) % cards.length; render(); });
      dots.forEach((dot,index) => dot.addEventListener('click', () => { current = index; render(); }));
      render();
    });
  }

  const initialize = scope => {
    if (scope.matches?.('[data-ctc-section]')) initRoot(scope);
    scope.querySelectorAll('[data-ctc-section]').forEach(initRoot);
  };
  initialize(document);
  document.addEventListener('shopify:section:load', event => initialize(event.target));
})();
